import { c as _c } from "react/compiler-runtime";
import type { Entity } from '@onefootprint/request-types/dashboard';
import { Form, LinkButton, Tooltip } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import { DocumentAction, DocumentDetails, useDocumentSummary } from '@/components/entity-onboarding-details';
import useEntityVault from '@/entities/hooks/use-entity-vault';
import DocumentDetailsDialog from '@/entity/components/content/components/document-details-dialog';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import useDocumentField from '@/entity/hooks/use-document-field';
import type { TransformedDocument } from '@/entity/hooks/use-documents';
import useDocumentsQueryParams from '@/entity/hooks/use-documents-query-params';
import useShowOnboarding from '../onboardings-shared/hooks/use-show-onboarding';
import AiImageFlagBadge from './components/ai-image-flag-badge';
import DocumentViewerContainer from './components/document-viewer-container';
export type DocumentItemProps = {
  document: TransformedDocument;
  entity: Entity;
  value: string;
  seqno?: number;
};
const DocumentItem = t0 => {
  const $ = _c(44);
  const {
    document,
    entity,
    value,
    seqno
  } = t0;
  const {
    t
  } = useTranslation("entity-details");
  let t1;
  if ($[0] !== entity.data || $[1] !== entity.id || $[2] !== seqno) {
    t1 = {
      entityAttributes: entity.data,
      entityId: entity.id,
      seqno
    };
    $[0] = entity.data;
    $[1] = entity.id;
    $[2] = seqno;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const vault = useEntityVault(t1);
  const form = useDecryptForm();
  let t2;
  if ($[4] !== entity || $[5] !== seqno) {
    t2 = {
      entity,
      seqno
    };
    $[4] = entity;
    $[5] = seqno;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    getProps,
    getRelatedDisByFormValue
  } = useDocumentField(t2);
  const field = vault.data ? getProps(document) : {
    isDecrypting: false,
    isDecryptable: false,
    isDecrypted: false,
    canSelect: false,
    showCheckbox: false,
    disabled: true
  };
  let t3;
  if ($[7] !== document || $[8] !== getRelatedDisByFormValue || $[9] !== vault.data) {
    t3 = vault.data ? getRelatedDisByFormValue(document) : [];
    $[7] = document;
    $[8] = getRelatedDisByFormValue;
    $[9] = vault.data;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const dis = t3;
  const {
    values
  } = useDocumentsQueryParams();
  const {
    scrollOnboardingIntoView
  } = useShowOnboarding();
  const {
    title
  } = useDocumentSummary(document);
  const open = values.documentId === document.id;
  const isCustomDocument = document.kind === "custom";
  const t4 = isCustomDocument ? "text-snippet-1" : "text-body-2";
  let t5;
  if ($[11] !== t4) {
    t5 = cx("wrap-anywhere text-primary", t4);
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const titleClassName = t5;
  let t6;
  if ($[13] !== dis || $[14] !== form) {
    t6 = event => {
      if (event.target.checked) {
        form.setFields(dis);
      } else {
        form.unsetFields(dis);
      }
    };
    $[13] = dis;
    $[14] = form;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleChange = t6;
  const t7 = !field.showCheckbox;
  let t8;
  if ($[16] !== t7) {
    t8 = cx("flex", {
      hidden: t7
    });
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== title || $[19] !== titleClassName) {
    t9 = <span className={titleClassName}>{title}</span>;
    $[18] = title;
    $[19] = titleClassName;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = dis.map(di => <input type="checkbox" disabled={field.disabled} key={`hidden-${di}`} {...form.register("fields")} value={di} />);
  let t11;
  if ($[21] !== t10) {
    t11 = <div className="hidden">{t10}</div>;
    $[21] = t10;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== document.uploads) {
    t12 = <AiImageFlagBadge uploads={document.uploads} />;
    $[23] = document.uploads;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== document.onboardingId || $[26] !== scrollOnboardingIntoView || $[27] !== t) {
    t13 = document.onboardingId && <LinkButton variant="label-3" className="shrink-0 transition-opacity duration-200 pointer-fine:pointer-events-none pointer-fine:opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100" onClick={() => scrollOnboardingIntoView(document.onboardingId ?? "", "documents")}>{t(_temp3)}</LinkButton>;
    $[25] = document.onboardingId;
    $[26] = scrollOnboardingIntoView;
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const T0 = DocumentDetails;
  const t14 = cx({
    "pl-7": field.showCheckbox
  });
  let t15;
  if ($[29] !== T0 || $[30] !== document || $[31] !== t14) {
    t15 = <T0 document={document} className={t14} />;
    $[29] = T0;
    $[30] = document;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== document || $[34] !== entity || $[35] !== title) {
    t16 = <span className="flex shrink-0 items-center"><DocumentAction entity={entity} document={document} title={title} /></span>;
    $[33] = document;
    $[34] = entity;
    $[35] = title;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== document || $[38] !== entity || $[39] !== isCustomDocument || $[40] !== open || $[41] !== seqno || $[42] !== vault) {
    t17 = open && vault.data && (isCustomDocument ? <DocumentViewerContainer key={document.id} document={document} entity={entity} vault={vault} seqno={seqno} /> : <DocumentDetailsDialog key={document.id} document={document} entity={entity} vault={vault} seqno={seqno} />);
    $[37] = document;
    $[38] = entity;
    $[39] = isCustomDocument;
    $[40] = open;
    $[41] = seqno;
    $[42] = vault;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  return <><div className="group flex items-center gap-3 py-1.5"><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><div className={t8}><Tooltip disabled={field.canSelect} content={field.isDecrypted ? t(_temp, {
              field: "Document"
            }) : t(_temp2)}><Form.Checkbox name="documentKinds" disabled={field.disabled} label={t9} value={value} onChange={handleChange} /></Tooltip>{t11}</div><p className={cx(titleClassName, {
            hidden: field.showCheckbox
          })}>{title}</p>{t12}{t13}</div>{t15}</div>{t16}</div>{t17}</>;
};
export default DocumentItem;
function _temp($) {
  return $.decrypt.fieldDecryptedNotAllowed;
}
function _temp2($_0) {
  return $_0.decrypt.notAllowed;
}
function _temp3($_1) {
  return $_1.fieldset.documents.viewOnboarding;
}